import React, { ReactElement, useCallback, useContext, useMemo, useState } from "react";

import { FreeCashRemovalPopupVariant } from "@swiggy-private/cart-types";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

import {
    FreeCashRemovalPopupContext,
    FreeCashRemovalPopupConfig,
    FreeCashRemovalPopupContextValue,
    FreeCashRemovalPopupAnalyticsParams,
} from "@cart-client/contexts/FreeCashRemovalPopupContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { impressionBackPressWarningD2C, clickBackPressWarningD2C } from "@cart-client/analytics/events/ToingLpg";
import { FREE_CASH_WARNING_POPUP_TEXT, FREE_CASH_ITEM_REMOVAL_POPUP_TEXT } from "@cart-client/constants/cartHeader";

const POPUP_ANALYTICS_TEXT = {
    [FreeCashRemovalPopupVariant.MENU_NAVIGATION]: FREE_CASH_WARNING_POPUP_TEXT,
    [FreeCashRemovalPopupVariant.ITEM_REMOVAL]: FREE_CASH_ITEM_REMOVAL_POPUP_TEXT,
};

export function FreeCashRemovalPopupContextProvider({ children }: { children: React.ReactNode }): ReactElement {
    const [popupConfig, setPopupConfig] = useState<FreeCashRemovalPopupConfig | null>(null);

    const { cartState } = useContext(CartContext);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const cartId = cartState?.localCart?.cart_id?.toString();
    const restaurantId = String(cartState?.localCart?.restaurantId ?? "");
    const expiryCashAmount =
        cartState?.reviewedCart?.data?.toing_presentational_config?.toing_price_comparison_data
            ?.success_state_halfcard_data?.free_cash_amt;

    const onPopupImpression = useCallback(
        (variant: FreeCashRemovalPopupVariant, analyticsParams?: FreeCashRemovalPopupAnalyticsParams) => {
            if (!analyticsParams) return;

            const copy = POPUP_ANALYTICS_TEXT[variant];
            impressionBackPressWarningD2C(impressionEvent, {
                text: copy.TITLE,
                subText: copy.DESCRIPTION,
                cta: JSON.stringify([copy.SECONDARY_CTA, copy.PRIMARY_CTA]),
                cartId,
                restaurantId,
                warningTrigger: analyticsParams.warningTrigger,
            });
        },
        [impressionEvent, cartId, restaurantId],
    );

    const onPopupClick = useCallback(
        (variant: FreeCashRemovalPopupVariant, userAction: string, analyticsParams?: FreeCashRemovalPopupAnalyticsParams) => {
            if (!analyticsParams) return;

            const copy = POPUP_ANALYTICS_TEXT[variant];
            clickBackPressWarningD2C(clickEvent, {
                text: copy.TITLE,
                subText: copy.DESCRIPTION,
                cta: userAction === "proceed" ? copy.PRIMARY_CTA : copy.SECONDARY_CTA,
                cartId,
                restaurantId,
                warningTrigger: analyticsParams.warningTrigger,
                userAction,
                expiryCashAmount,
            });
        },
        [clickEvent, cartId, restaurantId, expiryCashAmount],
    );

    const showPopup = useCallback(
        (config: FreeCashRemovalPopupConfig) => {
            onPopupImpression(config.variant, config.analyticsParams);

            const wrappedConfig: FreeCashRemovalPopupConfig = {
                ...config,
                onPrimaryClick: () => {
                    onPopupClick(config.variant, "proceed", config.analyticsParams);
                    config.onPrimaryClick();
                },
                onSecondaryClick: config.onSecondaryClick
                    ? () => {
                          onPopupClick(config.variant, "cancel", config.analyticsParams);
                          config.onSecondaryClick?.();
                      }
                    : undefined,
            };

            setPopupConfig(wrappedConfig);
        },
        [onPopupImpression, onPopupClick],
    );

    const hidePopup = useCallback(() => {
        setPopupConfig(null);
    }, []);

    const value = useMemo<FreeCashRemovalPopupContextValue>(
        () => ({ popupConfig, showPopup, hidePopup, onPopupImpression, onPopupClick }),
        [popupConfig, showPopup, hidePopup, onPopupImpression, onPopupClick],
    );

    return <FreeCashRemovalPopupContext.Provider value={value}>{children}</FreeCashRemovalPopupContext.Provider>;
}
